import { Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { QueryClientProvider } from "@tanstack/react-query";
import { getQueryClient } from "./@apis/queryClient";
import { bootstrapDeviceType } from "./lib/utils/boot";
import { detectDeviceType } from "./lib/utils/deviceUtils";
import * as Sentry from "@sentry/react";
import "./lib/bridge/handlePushToServer";
import "./lib/bridge/handleBackButton";
import "./lib/bridge/handleDeepLink";
import "./lib/bridge/handlePushMessage";
import "./lib/bridge/handleOtp";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";

declare global {
  interface Window {
    __APP_DISPLAY__?: { fontScale: number; density: number };
    setDisplayMetrics: (fontScale: number, density: number) => void;
  }
}

const DISPLAY_METRICS_STORAGE_KEY = "__app_display_metrics__";

const loadStoredMetrics = (): { fontScale: number; density: number } | null => {
  try {
    const raw = localStorage.getItem(DISPLAY_METRICS_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* empty */
  }
  return null;
};

const storeMetrics = (fontScale: number, density: number) => {
  try {
    localStorage.setItem(
      DISPLAY_METRICS_STORAGE_KEY,
      JSON.stringify({ fontScale, density }),
    );
  } catch {
    /* empty */
  }
};

const applyDisplayMetrics = (
  fontScale: number,
  density: number,
  persist: boolean = true,
) => {
  if (persist) storeMetrics(fontScale, density);
  const root = document.documentElement;
  root.style.fontSize = `${16 * fontScale}px`;
  root.style.setProperty("--app-font-scale", String(fontScale));
  root.style.setProperty("--app-density", String(density));
};

window.setDisplayMetrics = (fontScale, density) =>
  applyDisplayMetrics(fontScale, density, true);

const storedMetrics = loadStoredMetrics();
const nativeMetrics = window.__APP_DISPLAY__;
// window.open으로 열린 자식 창(예: AOS 마이페이지)은 __APP_DISPLAY__가
// 콜드 부팅 시점 값으로 stale일 수 있으므로, 런타임에 동기화된 localStorage 값을 우선한다.
const isChildWindow = !!window.opener;
const initialFontScale = isChildWindow
  ? (storedMetrics?.fontScale ?? nativeMetrics?.fontScale ?? 1)
  : (nativeMetrics?.fontScale ?? storedMetrics?.fontScale ?? 1);
const initialDensity = isChildWindow
  ? (storedMetrics?.density ?? nativeMetrics?.density ?? 1)
  : (nativeMetrics?.density ?? storedMetrics?.density ?? 1);
// 자식 창은 부모 탭의 최신 값을 덮어쓰지 않도록 storage 쓰기를 생략한다.
applyDisplayMetrics(initialFontScale, initialDensity, !isChildWindow);

// 폴드/펼침 등 리사이즈 시 inline 스타일이 유실되는 경우 마지막 메트릭을 재적용
window.addEventListener("resize", () => {
  const m = loadStoredMetrics();
  if (m) applyDisplayMetrics(m.fontScale, m.density, false);
});

bootstrapDeviceType();

// iOS는 input focus 시 계산 font-size가 16px 미만이면 자동 확대되므로,
// 플랫폼 클래스를 부여해 CSS에서 iOS만 최소 16px를 보장한다.
document.documentElement.classList.add(`is-${detectDeviceType()}`);

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: true,
  environment: import.meta.env.VITE_APP_MODE,
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  ignoreErrors: [/AxiosError/i],
});

const container = document.getElementById("root");
const root = ReactDOM.createRoot(container!, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.error("Uncaught error", error, errorInfo.componentStack);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
});
root.render(
  <QueryClientProvider client={getQueryClient()}>
    <Suspense fallback={<div>Loading...</div>}>
      <App />
    </Suspense>
  </QueryClientProvider>,
);
